import api from './api';
import {
  LoginRequest,
  LoginResponse,
  RegisterPlayerRequest,
  RegisterCoachRequest,
  RegisterSponsorRequest,
  ApiResponse,
  User,
} from '@types/index';

/**
 * Authentication API service
 */
class AuthService {
  /**
   * Login user
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('AuthService - Sending login request to /v1/auth/login with:', { email: data.email });
      const response = await api.post<ApiResponse<LoginResponse>>('/v1/auth/login', data);
      console.log('AuthService - Login response:', response);
      console.log('AuthService - Response data:', response.data);
      console.log('AuthService - Response data.data:', response.data.data);
      
      // Store tokens and user info
      if (response.data.data) {
        localStorage.setItem('accessToken', response.data.data.accessToken);
        localStorage.setItem('refreshToken', response.data.data.refreshToken || '');
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
        console.log('AuthService - Tokens stored in localStorage');
      } else {
        console.error('AuthService - No data in response:', response.data);
        throw new Error('Invalid response format from server');
      }
      
      return response.data.data!;
    } catch (error: any) {
      console.error('AuthService - Login error:', error);
      console.error('AuthService - Error response:', error.response);
      throw error;
    }
  }

  /**
   * Register player
   */
  async registerPlayer(data: RegisterPlayerRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/v1/auth/register/player',
      data
    );
    
    // Store tokens and user info
    if (response.data.data) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    
    return response.data.data!;
  }

  /**
   * Register coach
   */
  async registerCoach(data: RegisterCoachRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/v1/auth/register/coach',
      data
    );
    
    // Store tokens and user info
    if (response.data.data) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    
    return response.data.data!;
  }

  /**
   * Register sponsor partner account
   */
  async registerSponsor(data: RegisterSponsorRequest): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>(
      '/v1/auth/register/sponsor',
      data
    );

    if (response.data.data) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }

    return response.data.data!;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/v1/auth/logout');
    } finally {
      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<User>>('/v1/auth/me');
    return response.data.data!;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  /**
   * Get stored user info
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Forgot password - request reset link
   */
  async forgotPassword(email: string): Promise<{ message: string; resetLink?: string }> {
    const response = await api.post<ApiResponse<{ message: string; resetLink?: string }>>(
      '/v1/auth/forgot-password',
      { email }
    );
    return response.data.data || { message: response.data.message || '' };
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/v1/auth/reset-password', { token, newPassword });
  }
}

export default new AuthService();


